import {
  getLeoUuid,
  LeoUUID,
  getString,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class ValidateCSVAndUploadSpocsRPC implements
  LeoRPC<ValidateCSVAndUploadSpocsRPC.Request, ValidateCSVAndUploadSpocsRPC.Response, ValidateCSVAndUploadSpocsRPC.Errors.Errors>
{
  abstract execute(
    request: ValidateCSVAndUploadSpocsRPC.Request
  ): Promise<LeoRPCResult<ValidateCSVAndUploadSpocsRPC.Response, ValidateCSVAndUploadSpocsRPC.Errors.Errors>>;
}

export namespace ValidateCSVAndUploadSpocsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    documentId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isValidateCSVAndUploadSpocsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly documentId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    documentId: LeoUUID
    ) {
      this.projectId = projectId;
      this.documentId = documentId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const documentId = getLeoUuid(dto, "documentId", InvalidRequestDocumentIdError);
      return new Request(
        projectId,
        documentId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          documentId: this.documentId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ValidateCSVAndUploadSpocsRPC(projectId='${this.projectId}',documentId='${this.documentId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `ValidateCSVAndUploadSpocsRPC.Response()`;
  };

  export enum RPCError {
    InvalidFile = "INVALID_FILE",
    UnknownFile = "UNKNOWN_FILE",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidFileDTO {
      code: string;
      invalidFileDetails: string;
    }
    
    export class InvalidFile extends LeoRPCError<RPCError> {
      readonly isInvalidFile: boolean = true;
    
      readonly invalidFileDetails: string;
    
      constructor(
      invalidFileDetails: string
      ) {
        const code = RPCError.InvalidFile;
        super(code);
        InvalidFile.validateInvalidFileDetails(invalidFileDetails);
        this.invalidFileDetails = invalidFileDetails;
      }
    
      static fromDTO(dto: InvalidFileDTO): InvalidFile {
        const invalidFileDetails = getString(dto, "invalidFileDetails", InvalidResponseInvalidFileInvalidFileDetailsError);
        return new InvalidFile(
          invalidFileDetails
        );
      }
    
      toDTO(): InvalidFileDTO {
        const dto: InvalidFileDTO = {
          code: this.code,
          invalidFileDetails: this.invalidFileDetails
        };
        return dto;
      }
    
      private static validateInvalidFileDetails = (invalidFileDetails: string): void => {
        if(invalidFileDetails.trim().length === 0) {
          throw new InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError(
            `Attribute invalidFileDetails cannot be an empty string.`
          );
        }
      }
      
    }
    
    InvalidFile.prototype.toString = function toString(): string {
      return `ValidateCSVAndUploadSpocsRPC.INVALID_FILE(invalidFileDetails='${this.invalidFileDetails}')`;
    };
    
    
    export interface UnknownFileDTO {
      code: string;
    }
    
    export class UnknownFile extends LeoRPCError<RPCError> {
      readonly isUnknownFile: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnknownFile;
        super(code);
      }
    
    
      toDTO(): UnknownFileDTO {
        const dto: UnknownFileDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnknownFile.prototype.toString = function toString(): string {
      return `ValidateCSVAndUploadSpocsRPC.UNKNOWN_FILE()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ValidateCSVAndUploadSpocsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ValidateCSVAndUploadSpocsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidFile | UnknownFile | InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidFileDTO | UnknownFileDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidFile:
          return InvalidFile.fromDTO(dto as InvalidFileDTO);
        case RPCError.UnknownFile:
          return new UnknownFile();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends ValidateCSVAndUploadSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestDocumentIdError extends ValidateCSVAndUploadSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDocumentIdError";
  }
}


export class InvalidResponseInvalidFileError extends ValidateCSVAndUploadSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsError extends InvalidResponseInvalidFileError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError extends InvalidResponseInvalidFileInvalidFileDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError";
  }
}
export class InvalidResponseUnknownFileError extends ValidateCSVAndUploadSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnknownFileError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ValidateCSVAndUploadSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ValidateCSVAndUploadSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

